import { normalizeInstagramUrl } from "./instagram";
import { miniProfilePayload } from "./miniProfile";
import { normalizePhone } from "./phone";
import { validateOptionalContactFields } from "./contactValidation";

export const PROFILE_FIELD_IDS = {
  name: "profile-name-input",
  base_city: "home-city-input",
  role: "profile-role-section",
  dance_forms: "profile-dance-forms-section",
  contact_phone: "contact-phone-input",
};

export const PROFILE_HOST_FIELD_IDS = {
  school_name: "school-name-input",
  school_city: "school-city-input",
  contact_phone: "profile-payment-phone-input",
  contact_email: "profile-payment-email-input",
};

export const PROFILE_HOST_FIELD_ORDER = [
  "school_name",
  "school_city",
  "contact_phone",
  "contact_email",
];

export const PROFILE_FIELD_ORDER = ["name", "base_city", "contact_phone", "role", "dance_forms"];

export const ONBOARDING_STEP1_FIELD_ORDER = ["name"];
export const ONBOARDING_STEP2_FIELD_ORDER = ["role", "dance_forms"];

function sortedDanceForms(forms) {
  return [...(forms || [])].sort((a, b) => a.name.localeCompare(b.name));
}

export function serializePersonalSnapshot({ form, danceForm, miniProfile, showMiniOnEvents, account, organizer }) {
  organizer = account ?? organizer;
  return JSON.stringify({
    name: (form.name || "").trim(),
    contact_phone: normalizePhone(form.contact_phone),
    base_city: organizer ? (form.base_city || "").trim() : undefined,
    base_city_location: organizer ? form.base_city_location || null : undefined,
    personal_instagram: organizer ? normalizeInstagramUrl(form.personal_instagram) : undefined,
    role: danceForm.role || "",
    dance_forms: sortedDanceForms(danceForm.dance_forms),
    mini_profile: miniProfilePayload(miniProfile),
    show_mini_on_events: !!showMiniOnEvents,
  });
}

/** True while personal fields may still be filled by async hydration (locale/phone/name). */
export function personalFieldsPendingHydration({
  form,
  account,
  organizer,
  localeLoading,
  localeDefaultCity,
  guestPhone,
  organizerPhone,
  guestName,
}) {
  organizer = account ?? organizer;
  if (organizer && localeLoading) return true;
  const phone = guestPhone || organizerPhone || "";
  if (phone && !form.contact_phone?.trim()) return true;
  if (!organizer && guestName?.trim() && !form.name?.trim()) return true;
  if (organizer && localeDefaultCity && !form.base_city?.trim()) return true;
  return false;
}

export function serializeHostSnapshot({ form, defaultPayment }) {
  return JSON.stringify({
    runs_school: !!form.runs_school,
    school_name: (form.school_name || "").trim(),
    school_city: (form.school_city || "").trim(),
    school_city_location: form.school_city_location || null,
    school_bio: (form.school_bio || "").trim(),
    school_instagram: normalizeInstagramUrl(form.school_instagram),
    default_payment: defaultPayment,
  });
}

export function validatePersonalForm({ form, danceForm, account, organizer, onboarding = false }) {
  organizer = account ?? organizer;
  const errors = {};
  if (!form.name?.trim()) errors.name = "Enter your name.";
  if (organizer && !onboarding) {
    if (!form.base_city?.trim()) errors.base_city = "Enter your home city.";
    else if (!form.base_city_location) errors.base_city = "Select your city from the suggestions.";
  }
  if (!danceForm.role) errors.role = "Choose leader, follower, or both.";
  if (!danceForm.dance_forms?.length) errors.dance_forms = "Select at least one dance.";
  Object.assign(errors, validateOptionalContactFields({ phone: form.contact_phone }));
  return errors;
}

export function validateHostForm({ form }) {
  const errors = {};
  // School is optional; once a name is entered, city is required.
  if (form.school_name?.trim()) {
    if (!form.school_city?.trim()) errors.school_city = "Enter your school city.";
    else if (!form.school_city_location) {
      errors.school_city = "Select your city from the suggestions.";
    }
  }
  return errors;
}

export function validateHostPayment(payment) {
  return validateOptionalContactFields({
    phone: payment?.contact_phone,
    email: payment?.contact_email,
  });
}

export function validateOnboardingStep1({ form }) {
  const errors = {};
  if (!form.name?.trim()) errors.name = "Enter your name.";
  return errors;
}

export function validateOnboardingStep2({ danceForm }) {
  const errors = {};
  if (!danceForm.role) errors.role = "Choose leader, follower, or both.";
  if (!danceForm.dance_forms?.length) errors.dance_forms = "Select at least one dance.";
  return errors;
}

export function scrollToFirstProfileFieldError(errors, fieldOrder = PROFILE_FIELD_ORDER) {
  const reduceMotion =
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  for (const key of fieldOrder) {
    if (!errors[key]) continue;
    const el = document.getElementById(PROFILE_FIELD_IDS[key] || PROFILE_HOST_FIELD_IDS[key]);
    if (!el) continue;
    el.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "center" });
    const focusable = el.querySelector?.("input, button, [tabindex]");
    const target = focusable || el;
    if (typeof target.focus === "function") {
      try {
        target.focus({ preventScroll: true });
      } catch {
        target.focus();
      }
    }
    break;
  }
}
